import { useMutation } from "@tanstack/react-query";
import { editDealInfo } from "@/api/deals-api";
import { useCurrentDealStore } from "@/store/current-deal-store";
import { useDealsListStore } from "@/store/deals-list-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useBulkEditMaybe } from "@/components/deal-detail/fields/bulk-edit-context";
import { getErrorMessage } from "@/lib/api-error";
import { toast } from "sonner";
import type { DealTransformed } from "@/lib/schema/deal-schema";

type FieldValue = string | number | boolean | string[] | Record<string, unknown>;

/**
 * Hook wrapping the deal-info edit mutation: persists the change, merges the
 * fresh deal_info array into the current deal store, and fires a success
 * toast (callers can suppress the toast via options).
 */
export function useEditDealField() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { currentDeal, updateDeal } = useCurrentDealStore();
  const dealsListUpdateDeal = useDealsListStore((s) => s.updateDeal);
  const bulk = useBulkEditMaybe();

  // The mutation accepts an extra `silent` flag that flows into the success
  // handler — used by callers that fire many saves in a batch (e.g. Verify
  // all) and want to show a single summary toast instead of N "Updated"
  // toasts. The flag is dropped before the API call because the request
  // shape only carries the deal-info fields.
  type SaveArgs = {
    workspaceId: string;
    companyId: string;
    fieldName: string;
    fieldValue: FieldValue;
    silent?: boolean;
  };

  const mutation = useMutation({
    mutationFn: ({ silent: _silent, ...args }: SaveArgs) =>
      editDealInfo.editDealInfo(args),
    onSuccess: (data, variables) => {
      if (!data.updatedApplication?.deal_info || !currentDeal) return;
      const updates: Partial<DealTransformed> = {
        dealInfo: {
          ...currentDeal.dealInfo,
          deal: data.updatedApplication.deal_info as {
            field_name: string;
            field_value: string | number | string[];
            field_state: string;
          }[],
        },
      };
      if (variables.fieldName === "company_website") {
        updates.overview = {
          ...currentDeal.overview,
          website: variables.fieldValue as string,
        };
      }
      updateDeal(updates);
      // Mirror the dealInfo update into the deals list store so badges
      // that read field_state (e.g. "Review industries" on the pipeline
      // card) refresh immediately, not just after the next list refetch.
      dealsListUpdateDeal(currentDeal.id, updates);
      if (!variables.silent) toast.success("Updated");
    },
    onError: (error, variables) => {
      // Silent callers (e.g. Verify all) surface their own summary toast
      // covering both success and failure counts, so we skip the
      // per-field error toast here to avoid double-toasting.
      if (!variables.silent) {
        toast.error(getErrorMessage(error, "Failed to update"));
      }
    },
  });

  const save = async (
    fieldName: string,
    fieldValue: FieldValue,
    options?: { silent?: boolean }
  ) => {
    if (!currentDeal?.id || !activeWorkspaceId) {
      throw new Error("Missing deal or workspace context");
    }
    // Bulk-edit mode: stage instead of firing the mutation; the parent
    // region (e.g. ProfileTab's Save button) commits all staged edits in
    // one batch via Promise.all.
    if (bulk?.enabled) {
      bulk.stage(fieldName, fieldValue);
      return;
    }
    return mutation.mutateAsync({
      workspaceId: activeWorkspaceId,
      companyId: currentDeal.id,
      fieldName,
      fieldValue,
      silent: options?.silent,
    });
  };

  return { save, isSaving: mutation.isPending };
}
